import React, { useState, useMemo } from 'react';
import { useExitOrdersStore } from '../contexts/LTPStore/useExitOrdersStore';

const ExitSuccessModal = ({ isOpen, onClose, orders = [], actionType = "Exit" }) => {
    const exitOrders = useExitOrdersStore((state) => state.orders);
    const [filterStatus, setFilterStatus] = useState("ALL");

    // Pre-process orders to resolve status and live data
    const processedOrders = useMemo(() => {
        return orders.map((ord) => {
            const liveOrder = (exitOrders || []).find((o) => String(o?.order_id || o?.id) === String(ord?.orderId));

            // Use live status if available, else fallback to initial response
            const rawStatus = liveOrder
                ? (liveOrder?.status || liveOrder?.orderStatus || liveOrder?.status_no)
                : ord?.status;

            const statusUpper = String(rawStatus || "").toUpperCase();

            const isPending = ["PENDING", "TRANSIT", "OPEN", "6", "4", "WAITING"].some(s => statusUpper.includes(s));
            const isRejected = ["REJECTED", "FAILED", "ERRORED", "5"].some(s => statusUpper.includes(s));
            const isFilled = ["FILLED", "TRADED", "COMPLETE", "2"].some(s => statusUpper.includes(s));
            const isFailed = !ord?.orderId; // Failed to even place

            let category = "PENDING";
            if (isPending) category = "PENDING";
            if (isFilled) category = "FILLED";
            if (isRejected || isFailed) category = "REJECTED"; // Group API failures with rejected

            return {
                ...ord,
                liveOrder,
                displayStatus: rawStatus === "Waiting" ? "⏳" : rawStatus,
                category,
                isFailed
            };
        });
    }, [orders, exitOrders]);

    // Calculate counts
    const counts = useMemo(() => {
        const c = { ALL: processedOrders.length, REJECTED: 0, PENDING: 0, FILLED: 0 };
        processedOrders.forEach(o => {
            if (o.category === "REJECTED") c.REJECTED++;
            if (o.category === "PENDING") c.PENDING++;
            if (o.category === "FILLED") c.FILLED++;
        });
        return c;
    }, [processedOrders]);

    // Filter for display
    const displayedOrders = useMemo(() => {
        if (filterStatus === "ALL") return processedOrders;
        return processedOrders.filter(o => o.category === filterStatus);
    }, [processedOrders, filterStatus]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
            <div className="bg-[#14161f] rounded-lg p-6 w-[480px] border border-gray-700">

                {/* Header */}
                <h2
                    className={`text-xl font-semibold mb-4 ${orders.every(o => !o?.orderId)
                            ? "text-red-400"
                            : orders.some(o => !o?.orderId)
                                ? "text-yellow-400"
                                : "text-green-400"
                        }`}
                >
                    {orders.every(o => !o?.orderId)
                        ? `${actionType} Failed ❌`
                        : orders.some(o => !o?.orderId)
                            ? `${actionType} Partially Completed ⚠️`
                            : `${actionType} Processed ✅`}
                </h2>

                {/* Summary / Filter Bar */}
                <div className="flex items-center gap-2 mb-4 bg-gray-800/50 p-1 rounded-lg">
                    {[
                        { id: "ALL", label: "Total", color: "text-gray-300", bg: "bg-gray-700" },
                        { id: "FILLED", label: "Filled", color: "text-green-400", bg: "bg-green-900/30" },
                        { id: "PENDING", label: "Pending", color: "text-yellow-400", bg: "bg-yellow-900/30" },
                        { id: "REJECTED", label: "Rejected", color: "text-red-400", bg: "bg-red-900/30" },
                    ].map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => setFilterStatus(tab.id)}
                            className={`flex-1 flex flex-col items-center py-1.5 rounded text-xs font-medium transition-all ${filterStatus === tab.id
                                ? `${tab.bg} ring-1 ring-inset ring-white/10 shadow-sm`
                                : "hover:bg-gray-700/50"
                                }`}
                        >
                            <span className={`${filterStatus === tab.id ? tab.color : "text-gray-400"}`}>
                                {tab.label}
                            </span>
                            <span className={`text-sm font-bold ${filterStatus === tab.id ? "text-white" : "text-gray-500"}`}>
                                {counts[tab.id]}
                            </span>
                        </button>
                    ))}
                </div>

                <div className="max-h-64 overflow-y-auto border border-gray-700 rounded mb-4 custom-scrollbar">
                    {displayedOrders.length === 0 ? (
                        <div className="p-8 text-center text-gray-500 text-sm">
                            No orders in this category.
                        </div>
                    ) : (
                        displayedOrders.map((ord, idx) => {
                            const { isFailed, displayStatus, liveOrder, category } = ord;

                            let statusColor = "bg-gray-600 text-white";
                            if (category === "FILLED") statusColor = "bg-green-600 text-white";
                            if (category === "REJECTED") statusColor = "bg-red-600 text-white";
                            if (category === "PENDING") statusColor = "bg-yellow-600 text-white";

                            return (
                                <div
                                    key={idx}
                                    className={`px-3 py-2 border-b last:border-0 text-xs ${isFailed
                                        ? "border-red-500/20 bg-red-900/5"
                                        : "border-gray-700/50 hover:bg-white/5 transition-colors"
                                        }`}
                                >
                                    <div className="flex justify-between items-start">
                                        {/* LEFT: Symbol + Client Info */}
                                        <div className="flex flex-col gap-0.5">
                                            <span className="font-bold text-gray-200 text-sm">{ord?.symbol}</span>
                                            <div className="flex items-center gap-2 text-[10px] text-gray-500 font-mono">
                                                <span className="text-gray-400">{ord?.clientId || ord?.clientid}</span>
                                                <span className="text-gray-600">|</span>
                                                <span>{ord?.broker?.toUpperCase()}</span>
                                            </div>
                                        </div>

                                        {/* RIGHT: Status + Qty + ID */}
                                        <div className="flex flex-col items-end gap-1">
                                            {isFailed ? (
                                                <span className="text-red-400 font-mono font-bold bg-red-900/20 px-1 py-0.5 rounded">FAILED</span>
                                            ) : (
                                                <span className={`font-mono text-[10px] font-bold px-1.5 py-0.5 rounded shadow-sm ${statusColor}`}>
                                                    {displayStatus}
                                                </span>
                                            )}

                                            <div className="flex flex-col items-end text-[10px] text-gray-400 font-mono">
                                                {liveOrder && (
                                                    <span>Qty: {liveOrder?.qty} <span className="text-gray-600">/</span> Rem: {liveOrder?.remaining_qty ?? 0}</span>
                                                )}
                                                {liveOrder?.limit_price && (
                                                    <span>Limit: {liveOrder?.limit_price}</span>
                                                )}
                                                <span className="text-gray-600">ID: {String(ord?.orderId)}</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Rejection Reason (from Websocket or API response) */}
                                    {(liveOrder?.reject_reason || liveOrder?.message || ord?.error) && (category === "REJECTED" || isFailed) && (
                                        <div className="text-[10px] text-red-300 mt-1.5 bg-red-950/50 border border-red-900/30 p-1.5 rounded flex items-start gap-1">
                                            <span className="shrink-0">⚠️</span>
                                            <span className="break-words">{liveOrder?.reject_reason || liveOrder?.message || ord?.error}</span>
                                        </div>
                                    )}
                                </div>
                            );
                        })
                    )}
                </div>

                <div className="flex justify-end">
                    <button
                        onClick={onClose}
                        className={`px-4 py-2 rounded text-sm font-medium text-white transition-colors ${orders.some(o => !o.orderId)
                            ? "bg-yellow-600 hover:bg-yellow-700"
                            : "bg-green-600 hover:bg-green-700"
                            }`}
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ExitSuccessModal;
